"use client";

// Primary CTA pill with static arrow-circle and gradient-background-shadow
// hover by default (WEB-651). Opt into gradient-hover via hoverVariant.

import { Button as ButtonPrimitive } from "@base-ui/react/button";
import { cva } from "class-variance-authority";
import type { VariantProps } from "class-variance-authority";
import { ArrowRight } from "iconoir-react";
import * as React from "react";

import {
  resetBackgroundShadowPosition,
  updateBackgroundShadowPosition,
  updateLightLeakPosition,
} from "@/lib/button-light-leak";
import { cn } from "@/lib/utils";

const arrowCtaButtonVariants = cva(
  [
    "inline-flex shrink-0 items-center justify-center gap-3 rounded-full font-medium text-white",
    "focus-visible:outline-none",
    "active:scale-[0.97] motion-reduce:active:scale-100",
    "disabled:pointer-events-none disabled:cursor-not-allowed",
  ],
  {
    variants: {
      hoverVariant: {
        /** Outline + text/circle gradient animation on hover. */
        "gradient-hover": "button-primary-gradient-hover",
        /** Solid button with pointer-positioned background glow on hover (default). */
        "gradient-background-shadow": [
          "group button-primary-gradient-background-shadow bg-pulse-700",
          "active:scale-100 motion-reduce:active:scale-100",
        ],
      },
      layout: {
        /** Default pill — arrow visible at all breakpoints. */
        pill: "py-1.5 pr-[7px] pl-4",
        /** Email capture pill button — Figma 4074:23618; shared by hero, funding eligibility, etc. */
        "hero-form": "justify-start py-1.5 pr-[7px] pl-4",
      },
      labelSize: {
        lg: "text-ds-label-lg",
        md: "text-[14px]",
        inherit: "",
      },
    },
    defaultVariants: {
      hoverVariant: "gradient-background-shadow",
      layout: "pill",
      labelSize: "lg",
    },
  }
);

function StaticCtaArrowCircle({
  className,
  iconClassName,
  strokeWidth = 1.5,
  animated = false,
}: {
  className?: string;
  iconClassName?: string;
  strokeWidth?: number;
  /** Dual-arrow slide on group hover (Codrops skoll pattern). */
  animated?: boolean;
}) {
  const arrowClass = cn(
    "size-4",
    iconClassName,
    animated &&
      "transition-transform duration-300 ease-[cubic-bezier(0.7,0,0.2,1)] motion-safe:group-hover:translate-x-[200%]"
  );

  return (
    <span
      aria-hidden
      data-slot="button-gradient-arrow-circle"
      className={cn(
        animated && "overflow-hidden transition-colors group-hover:bg-white/30",
        className
      )}
    >
      <span
        data-slot="button-gradient-arrow-icon"
        className={cn(
          animated &&
            "relative inline-flex size-4 items-center justify-center overflow-hidden"
        )}
      >
        <ArrowRight className={arrowClass} strokeWidth={strokeWidth} />
        {animated ? (
          <ArrowRight
            className={cn(
              arrowClass,
              "absolute -translate-x-[200%] motion-safe:group-hover:translate-x-0"
            )}
            strokeWidth={strokeWidth}
          />
        ) : null}
      </span>
    </span>
  );
}

export interface ArrowCtaButtonProps
  extends ButtonPrimitive.Props, VariantProps<typeof arrowCtaButtonVariants> {
  /** Hide the arrow on viewports below md (hero email capture pattern). */
  hideArrowOnMobile?: boolean;
  arrowClassName?: string;
  arrowIconClassName?: string;
  arrowStrokeWidth?: number;
}

const ArrowCtaButton = React.forwardRef<HTMLButtonElement, ArrowCtaButtonProps>(
  (
    {
      className,
      hoverVariant,
      layout,
      labelSize,
      children,
      hideArrowOnMobile = false,
      arrowClassName,
      arrowIconClassName,
      arrowStrokeWidth,
      render,
      nativeButton,
      onPointerEnter,
      onPointerMove,
      onPointerLeave,
      ...props
    },
    ref
  ) => {
    const resolvedHoverVariant = hoverVariant ?? "gradient-background-shadow";
    const tracksBackgroundShadow =
      resolvedHoverVariant === "gradient-background-shadow";

    const trackLightLeak = (event: React.PointerEvent<HTMLElement>) => {
      if (tracksBackgroundShadow) {
        updateBackgroundShadowPosition(
          event.currentTarget,
          event.clientX,
          event.clientY
        );
        return;
      }

      updateLightLeakPosition(event.currentTarget, event.clientX);
    };

    return (
      <ButtonPrimitive
        ref={ref}
        data-slot="arrow-cta-button"
        className={cn(
          arrowCtaButtonVariants({ hoverVariant, layout, labelSize }),
          className
        )}
        nativeButton={nativeButton ?? !render}
        render={render}
        onPointerEnter={(event) => {
          trackLightLeak(event);
          onPointerEnter?.(event);
        }}
        onPointerMove={(event) => {
          trackLightLeak(event);
          onPointerMove?.(event);
        }}
        onPointerLeave={(event) => {
          if (tracksBackgroundShadow) {
            resetBackgroundShadowPosition(event.currentTarget);
          }
          onPointerLeave?.(event);
        }}
        {...props}
      >
        <span data-slot="button-gradient-label">{children}</span>
        <StaticCtaArrowCircle
          animated={tracksBackgroundShadow}
          className={cn(hideArrowOnMobile && "hidden md:flex", arrowClassName)}
          iconClassName={arrowIconClassName}
          strokeWidth={arrowStrokeWidth}
        />
      </ButtonPrimitive>
    );
  }
);
ArrowCtaButton.displayName = "ArrowCtaButton";

export { ArrowCtaButton, arrowCtaButtonVariants, StaticCtaArrowCircle };
